import { type AnimationConfig, parseAnimationConfig } from "./animationConfig";
import type { FrameConfig } from "./frameConfig";

/**
 * String form of `Direction` accepted in raw tileset data. Mirrors the values
 * used in the original JSON config so each entry's `pointsTo` array can be
 * authored with plain string literals.
 */
export type RawDirection = "N" | "NE" | "E" | "SE" | "S" | "SW" | "W" | "NW";

export interface TilesetConfigEntry {
	key: string;
	allowTransform: boolean;
	animations: AnimationConfig[];
}

/**
 * Raw shape of a frame as it appears in TS/JSON tileset data files, before
 * being normalized by `parseFrameConfig`. Only `interval` is required; every
 * other field has a default applied during parsing.
 */
export interface RawFrameConfig {
	interval: number;
	filename?: string | null;
	rotation?: number;
	verticalFlip?: boolean;
	horizontalFlip?: boolean;
	width?: number;
	height?: number;
	offsetX?: number;
	offsetY?: number;
	character?: string | null;
	foreground?: string | null;
	background?: string | null;
}

/**
 * Raw shape of an animation as it appears in TS/JSON tileset data files,
 * before being normalized by `parseAnimationConfig`. Only `frames` is
 * required; every other field has a default applied during parsing.
 */
export interface RawAnimationConfig {
	frames: RawFrameConfig[];
	loop?: boolean;
	rotation?: number;
	verticalFlip?: boolean;
	horizontalFlip?: boolean;
	width?: number;
	height?: number;
	offsetX?: number;
	offsetY?: number;
	pointsTo?: RawDirection[];
	tags?: Record<string, unknown>;
}

/**
 * Raw shape of a tileset entry as it appears in TS/JSON tileset data files,
 * before being normalized by `parseTilesetConfigEntry`. The discriminated
 * union mirrors the four shorthand formats the parser accepts: full
 * `animations` array, single `sprite`, multiple `sprites`, or a
 * `character`-based text entry.
 */
export type RawTilesetEntry = { key: string; allowTransform?: boolean } & (
	| { animations: RawAnimationConfig[] }
	| { sprite: string }
	| { sprites: string[] }
	| { character: string; foreground?: string | null; background?: string | null }
);

/**
 * Normalizes a `RawTilesetEntry` into the canonical `TilesetConfigEntry`:
 * expands the four shorthand shapes (`animations` / `sprite` / `sprites` /
 * `character`) into a uniform `animations[]` and fills in defaults so runtime
 * code never has to deal with optional fields. The shorthands exist so static
 * sprites can be authored as one line instead of ~15.
 */
export function parseTilesetConfigEntry(json: any): TilesetConfigEntry {
	if (!json.key) {
		throw new Error("TilesetConfigEntry: key is required");
	}

	const key: string = json.key;
	const allowTransform: boolean = json.allowTransform ?? false;
	let animations: AnimationConfig[];

	if (json.animations && Array.isArray(json.animations)) {
		animations = json.animations.map(parseAnimationConfig);
	} else if (json.sprite) {
		animations = [makeSingleSpriteAnimation(json.sprite)];
	} else if (json.sprites && Array.isArray(json.sprites)) {
		animations = json.sprites.map((s: string) => makeSingleSpriteAnimation(s));
	} else if (json.character) {
		animations = [makeSingleCharacterAnimation(json.character, json.foreground ?? null, json.background ?? null)];
	} else {
		animations = [];
	}

	return { key, allowTransform, animations };
}

function makeDefaultFrame(): FrameConfig {
	return {
		filename: null,
		interval: 0,
		rotation: 0,
		verticalFlip: false,
		horizontalFlip: false,
		width: 1.0,
		height: 1.0,
		offsetX: 0,
		offsetY: 0,
		character: null,
		foreground: null,
		background: null,
	};
}

function makeDefaultAnimation(frames: FrameConfig[]): AnimationConfig {
	return {
		loop: true,
		rotation: 0,
		verticalFlip: false,
		horizontalFlip: false,
		width: 1.0,
		height: 1.0,
		offsetX: 0,
		offsetY: 0,
		pointsTo: [],
		tags: {},
		frames,
	};
}

function makeSingleSpriteAnimation(filename: string): AnimationConfig {
	const frame = makeDefaultFrame();
	frame.filename = filename;
	return makeDefaultAnimation([frame]);
}

function makeSingleCharacterAnimation(
	character: string,
	foreground: string | null,
	background: string | null,
): AnimationConfig {
	const frame = makeDefaultFrame();
	frame.character = character;
	frame.foreground = foreground;
	frame.background = background;
	return makeDefaultAnimation([frame]);
}
